import { getCurrentUser } from '@/lib/auth-utils'
import { CURRENCY_CODE } from '@/lib/currency'
import { ROLE_ADMIN } from '@/lib/permissions'
import type { Invoice } from '@/models'
import { auditLog } from '@/services/auditLog'
import { neon } from '@/services/neon'
import { fetchProductByName } from '@/services/products'
import { getStorageUrl, STORAGE_FOLDERS, uploadStorageFile } from '@/services/storage'

export const SUBSCRIPTION_PRICE = 499
export const SUBSCRIPTION_PLAN = 'Premium Monthly'
export const SUBSCRIPTION_CURRENCY = CURRENCY_CODE

export async function fetchMyInvoices(): Promise<Invoice[]> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated')

  const { data, error } = await neon
    .from('invoices')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })

  if (error) throw new Error(error.message)
  return (data as Invoice[]) || []
}

export async function fetchAllInvoices(status?: string): Promise<Invoice[]> {
  const user = getCurrentUser()
  if (!user?.id || user.role !== ROLE_ADMIN) throw new Error('Not authorized')

  let query = neon.from('invoices').select('*').order('created_at', { ascending: false })
  if (status) query = query.eq('status', status)

  const { data, error } = await query
  if (error) throw new Error(error.message)
  return (data as Invoice[]) || []
}

export async function createInvoice(productName?: string, voucherCode?: string): Promise<Invoice> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated')

  const product = productName ? await fetchProductByName(productName) : null
  const originalAmount = product?.price || SUBSCRIPTION_PRICE
  let amount = originalAmount
  let discountAmount = 0
  let voucherId: string | null = null
  let cleanVoucherCode: string | null = null

  if (voucherCode?.trim()) {
    const { validateVoucher } = await import('@/services/vouchers')
    const result = await validateVoucher(voucherCode.trim(), originalAmount)
    if (!result.valid) throw new Error(result.error || 'Invalid voucher')
    discountAmount = result.discountAmount || 0
    amount = result.finalAmount ?? originalAmount
    voucherId = result.voucher?.id || null
    cleanVoucherCode = result.voucher?.code || voucherCode.trim().toUpperCase()
  }

  const { data, error } = await neon
    .from('invoices')
    .insert({
      user_id: user.id,
      plan: product?.name || SUBSCRIPTION_PLAN,
      amount,
      original_amount: originalAmount,
      discount_amount: discountAmount,
      voucher_code: cleanVoucherCode,
      product_id: product?.id || null,
      product_name: product?.name || null,
    })
    .select()
    .single()

  if (error) throw new Error(error.message)
  const invoice = data as Invoice

  if (voucherId && discountAmount > 0) {
    const { redeemVoucher } = await import('@/services/vouchers')
    try {
      await redeemVoucher(voucherId, user.id, invoice.id, discountAmount)
    } catch {
      // redemption failure shouldn't block invoice creation (log)
    }
  }

  return invoice
}

export async function uploadReceipt(
  invoiceId: string,
  file: File,
  referenceNumber: string,
): Promise<Invoice> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated')

  const { key } = await uploadStorageFile(
    STORAGE_FOLDERS.RECEIPTS,
    `${invoiceId}-${Date.now()}-${file.name}`,
    file,
    file.type,
  )

  const { data, error } = await neon
    .from('invoices')
    .update({ receipt_url: key, reference_number: referenceNumber })
    .eq('id', invoiceId)
    .eq('user_id', user.id)
    .select()
    .single()

  if (error) throw new Error(error.message)
  return data as Invoice
}

export async function verifyInvoice(
  invoiceId: string,
  action: 'approve' | 'reject',
  notes?: string,
): Promise<Invoice> {
  const user = getCurrentUser()
  if (!user?.id || user.role !== ROLE_ADMIN) throw new Error('Not authorized')

  const update: Record<string, any> = {
    status: action === 'approve' ? 'paid' : 'rejected',
    verified_by: user.id,
    notes: notes || null,
  }

  if (action === 'approve') {
    update.paid_at = new Date().toISOString()
  }

  if (action === 'approve') {
    const endDate = new Date()
    endDate.setMonth(endDate.getMonth() + 1)
    const { data: invoice } = await neon
      .from('invoices')
      .select('user_id')
      .eq('id', invoiceId)
      .single()
    const userId = (invoice as { user_id?: string } | null)?.user_id
    if (userId) {
      await neon
        .from('users')
        .update({
          subscription_status: 'active',
          subscription_end_date: endDate.toISOString(),
        })
        .eq('id', userId)
    }
  }

  const { data, error } = await neon
    .from('invoices')
    .update(update)
    .eq('id', invoiceId)
    .select()
    .single()

  if (error) throw new Error(error.message)

  auditLog(user.id, 'invoices', invoiceId, 'update', {
    status: { to: update.status },
    verified_by: { to: update.verified_by },
  })

  return data as Invoice
}

export function getReceiptUrl(invoice: Invoice): string {
  const url = invoice.receipt_url
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url
  return getStorageUrl(url)
}
